import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AppBskyFeedDefs } from '@atproto/api';
import { useSession } from '@/lib/session';
import { HeartIcon, RepeatIcon, ReplyIcon, ShareIcon } from './icons';
import { useCompose } from './compose-modal';
import { formatDateTime } from '@/lib/format-datetime';
import { didFromUri, postDetailPath, rkeyFromUri } from '@/lib/uri';
import { useColumnNav } from './column-detail';
import type { PostRecordShape } from './post-article';

interface PostMetricsProps {
  post: AppBskyFeedDefs.PostView;
  /** スレッド展開トグルを表示する場合。ハンドラを渡すと ▼/▲ ボタンが現れる。 */
  onToggleThread?: () => void;
  threadExpanded?: boolean;
}

/**
 * 投稿カード下部のアクション行。いいね/リポスト/リプライ + 右端に投稿日時。
 * - いいね: agent.like / agent.deleteLike をトグル
 * - リポスト: agent.repost / agent.deleteRepost をトグル
 * - リプライ: /compose にリプライ先を持たせて遷移
 * - 日時: アクション行の右端に小さく右揃え。タップで投稿詳細へ遷移
 * - スレッドトグル (任意): スレッドを持つ投稿のみ一段下の右端に表示
 */
export function PostMetrics({ post, onToggleThread, threadExpanded }: PostMetricsProps) {
  const session = useSession();
  const columnNav = useColumnNav();
  const { openCompose } = useCompose();
  const agent = session.agent;

  const [likeUri, setLikeUri] = useState<string | undefined>(post.viewer?.like);
  const [repostUri, setRepostUri] = useState<string | undefined>(post.viewer?.repost);
  const [likeCount, setLikeCount] = useState<number>(post.likeCount ?? 0);
  const [repostCount, setRepostCount] = useState<number>(post.repostCount ?? 0);
  const [busy, setBusy] = useState<'' | 'like' | 'repost'>('');

  async function toggleLike(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!agent || busy) return;
    setBusy('like');
    const prevUri = likeUri;
    const prevCount = likeCount;
    // 楽観的更新
    if (prevUri) {
      setLikeUri(undefined);
      setLikeCount(prevCount - 1);
    } else {
      setLikeUri('pending');
      setLikeCount(prevCount + 1);
    }
    try {
      if (prevUri && prevUri !== 'pending') {
        await agent.deleteLike(prevUri);
      } else {
        const res = await agent.like(post.uri, post.cid);
        setLikeUri(res.uri);
      }
    } catch (err) {
      console.warn('like toggle failed', err);
      // 戻す
      setLikeUri(prevUri);
      setLikeCount(prevCount);
    } finally {
      setBusy('');
    }
  }

  async function toggleRepost(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!agent || busy) return;
    setBusy('repost');
    const prevUri = repostUri;
    const prevCount = repostCount;
    if (prevUri) {
      setRepostUri(undefined);
      setRepostCount(prevCount - 1);
    } else {
      setRepostUri('pending');
      setRepostCount(prevCount + 1);
    }
    try {
      if (prevUri && prevUri !== 'pending') {
        await agent.deleteRepost(prevUri);
      } else {
        const res = await agent.repost(post.uri, post.cid);
        setRepostUri(res.uri);
      }
    } catch (err) {
      console.warn('repost toggle failed', err);
      setRepostUri(prevUri);
      setRepostCount(prevCount);
    } finally {
      setBusy('');
    }
  }

  function onReply(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const rec = post.record as { reply?: { root: { uri: string; cid: string } }; text?: string };
    const root = rec.reply?.root ?? { uri: post.uri, cid: post.cid };
    openCompose({
      parent: { uri: post.uri, cid: post.cid },
      root,
      author: post.author.handle,
      text: rec.text ?? '',
    });
  }

  const liked = !!likeUri;
  const reposted = !!repostUri;
  const ts = (post.record as PostRecordShape).createdAt ?? post.indexedAt;
  const detailPath = postDetailPath(post.author.handle, post.uri);

  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 行が仮想フィードでアンマウントされても、遅延した setCopied(false) が走らないよう掃除。
  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);

  // 投稿を共有する。公開リンク (bsky.app) を使い、誰でも開ける URL にする。
  // モバイルは OS の共有シート (navigator.share)、無ければクリップボードにコピー。
  async function onShare(e: React.MouseEvent) {
    e.stopPropagation();
    // handle.invalid (未解決アカウント) は bsky.app で開けないので DID を使う。
    // bsky.app は /profile/<did>/post/<rkey> も解決する。
    const handle = post.author.handle;
    const actor = handle && handle !== 'handle.invalid' ? handle : didFromUri(post.uri);
    const url = `https://bsky.app/profile/${actor}/post/${rkeyFromUri(post.uri)}`;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ url });
      } catch {
        /* キャンセル/失敗時は何もしない (誤コピーを避ける) */
      }
      return;
    }
    try {
      await navigator.clipboard?.writeText(url);
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      /* no-op */
    }
  }

  return (
    <div style={{ marginTop: '0.5em' }}>
      {/* アクション行: リプ/リポスト/いいね + 右端に日時 (小さく右揃え)。
          行・各要素とも nowrap で、狭いカラムでも絶対に折り返さない。 */}
      <div style={{ display: 'flex', gap: '0.7em', alignItems: 'center', flexWrap: 'nowrap' }}>
        <MetricButton onClick={onReply} ariaLabel="返信" count={post.replyCount ?? 0}>
          <ReplyIcon size={15} />
        </MetricButton>
        <MetricButton onClick={toggleRepost} ariaLabel={reposted ? 'リポスト解除' : 'リポスト'} count={repostCount} active={reposted} activeColor="#4caf7d">
          <RepeatIcon size={15} />
        </MetricButton>
        <MetricButton onClick={toggleLike} ariaLabel={liked ? 'いいね解除' : 'いいね'} count={likeCount} active={liked} activeColor="#ff6b9a">
          <HeartIcon size={15} />
        </MetricButton>
        <MetricButton onClick={onShare} ariaLabel="共有" {...(copied ? { count: '✓' } : {})} active={copied} activeColor="#4caf7d">
          <ShareIcon size={15} />
        </MetricButton>
        <Link
          to={detailPath}
          onClick={(e) => {
            e.stopPropagation();
            // カラム内なら route 遷移せず、そのカラムに詳細を積む (TL は下でマウントの
            // まま = スクロール保持)。修飾キー/中クリックは新規タブ等の既定挙動に任せる。
            if (columnNav && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
              e.preventDefault();
              columnNav.openPost(post.uri, post.author.handle);
            }
          }}
          style={{
            marginLeft: 'auto',
            padding: '0.2em 0.1em',
            fontSize: '0.68em',
            fontFamily: 'ui-monospace, monospace',
            color: 'var(--color-muted)',
            textDecoration: 'none',
            borderBottom: 'none',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          title="投稿詳細を開く"
        >
          <time dateTime={ts}>{formatDateTime(ts)}</time>
        </Link>
      </div>
      {/* スレッドがあるときはトグルを一段下の右端へ */}
      {onToggleThread && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.2em' }}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleThread();
            }}
            aria-label={threadExpanded ? 'スレッドを閉じる' : 'スレッドを展開'}
            title={threadExpanded ? 'スレッドを閉じる' : 'スレッドを展開'}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '0.15em 0.4em',
              color: threadExpanded ? 'var(--color-accent)' : 'var(--color-muted)',
              fontSize: '0.78em',
              cursor: 'pointer',
              borderRadius: 4,
              fontFamily: 'ui-monospace, monospace',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-overlay-soft)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            {threadExpanded ? '▲ 閉じる' : '▼ スレッド'}
          </button>
        </div>
      )}
    </div>
  );
}

function MetricButton({
  onClick,
  ariaLabel,
  count,
  children,
  active = false,
  activeColor,
}: {
  onClick: (e: React.MouseEvent) => void;
  ariaLabel: string;
  /** 数値カウント / 短い文字 (共有ボタンのコピー完了 ✓ 等)。未指定なら数字を出さない。 */
  count?: number | string;
  children: React.ReactNode;
  active?: boolean;
  activeColor?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3em',
        flexShrink: 0,
        background: 'transparent',
        border: 'none',
        padding: '0.15em 0.3em',
        color: active && activeColor ? activeColor : 'var(--color-muted)',
        fontSize: '0.78em',
        cursor: 'pointer',
        boxShadow: 'none',
        borderRadius: 4,
        whiteSpace: 'nowrap',
        transition: 'color 120ms ease, background-color 120ms ease',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-overlay-soft)')}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
    >
      {children}
      {count !== undefined && count !== '' && <span>{count}</span>}
    </button>
  );
}
